import { useCallback, useEffect, useMemo, useState } from "react";
import { CircleHelp, ExternalLink, LogOut, Search } from "lucide-react";
import { Outlet, ScrollRestoration, useLocation, useNavigate, useOutlet } from "react-router";
import { HandoffLink, useAuth, usePermission, useSessionRefresh, useUserPhoto } from "@ncp/api-client";
import { AppVersion, ReportIssue, SessionGuard, useVersionInfo } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { AppShell, Avatar, Badge, Button, ConfirmProvider, GovBar, MOD, PageTransition, Shortcut, SideNav, Toaster, Tooltip, runSaveHandlers, useKeyboardShortcuts, type NavSection, useSidebarCollapsed } from "@ncp/ui";
import { ALL_NAV_ITEMS, CONSOLE_NAV, navAllowed } from "./nav";
import { ConsoleProvider, useConsole } from "./console-context";
import { CommandPalette } from "../components/CommandPalette";
import { HelpDrawer } from "../components/HelpDrawer";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ShortcutsDialog } from "../components/ShortcutsDialog";
import { backofficeHref } from "../lib/links";

const SIDEBAR_STORAGE_KEY = "ncp.admin.sidebar";
const BUILD_ENVIRONMENT = (import.meta.env.VITE_NCP_ENVIRONMENT as string | undefined) ?? (import.meta.env.DEV ? "development" : "production");
const ENVIRONMENT_NAMES: Record<string, string> = { dev: "development", prod: "production" };

export function RootLayout() {
  const { pathname } = useLocation();
  // Permissions are data: re-sync the session user so role edits made in the console apply without a new sign-in.
  useSessionRefresh();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export function ConsoleLayout() {
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  const navigate = useNavigate();
  const { can } = usePermission();

  const openPalette = useCallback(() => setPalette(true), []);
  const openHelp = useCallback(() => setHelp(true), []);

  useKeyboardShortcuts({
    onPalette: openPalette,
    onSearch: openPalette,
    onHelp: () => setShortcuts(true),
    onGo: (letter) => {
      const target = ALL_NAV_ITEMS.find((i) => i.shortcut === letter && navAllowed(i, can));
      if (target) navigate(target.href);
    },
    onSave: () => {
      runSaveHandlers();
    },
  });

  return (
    <ConsoleProvider onOpenPalette={openPalette} onOpenHelp={openHelp}>
      <Shell onOpenPalette={openPalette} onOpenHelp={openHelp} />
      <PaletteHost open={palette} onOpenChange={setPalette} />
      <HelpHost open={help} onOpenChange={setHelp} />
      <ShortcutsDialog open={shortcuts} onOpenChange={setShortcuts} />
    </ConsoleProvider>
  );
}

function PaletteHost({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { actions } = useConsole();
  return <CommandPalette open={open} onOpenChange={onOpenChange} actions={actions} />;
}

function HelpHost({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { helpTopic } = useConsole();
  return <HelpDrawer open={open} onOpenChange={onOpenChange} topic={helpTopic} />;
}

function Shell({ onOpenPalette, onOpenHelp }: { onOpenPalette: () => void; onOpenHelp: () => void }) {
  const { t } = useTranslation();
  const { locale, toggle, dir } = useLocale();
  const { user, clear } = useAuth();
  const photo = useUserPhoto(user?.photoUrl);
  const { can } = usePermission();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The element is taken per route so the page fading out keeps rendering itself; a live <Outlet /> there would mount the incoming page twice.
  const outlet = useOutlet();
  const serverEnvironment = useVersionInfo().data?.environment;
  const environment = serverEnvironment ? (ENVIRONMENT_NAMES[serverEnvironment] ?? serverEnvironment) : BUILD_ENVIRONMENT;

  const go = (to: string) => () => navigate(to);
  const signedOut = useCallback(() => navigate("/login"), [navigate]);
  const active = (to: string) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  const [collapsed, toggleCollapsed] = useSidebarCollapsed(SIDEBAR_STORAGE_KEY);

  const sections = useMemo<NavSection[]>(
    () =>
      CONSOLE_NAV.map((section) => ({
        title: section.title ? t(`admin:nav.${section.title}`) : undefined,
        items: section.items
          .filter((i) => navAllowed(i, can))
          .map((i) => ({
            key: i.key,
            label: t(`admin:nav.${i.label}`),
            icon: i.icon,
            href: i.href,
            active: active(i.href),
            onClick: go(i.href),
            badge: i.ai ? <Badge variant="ai" size="sm">AI</Badge> : undefined,
          })),
      })).filter((s) => s.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, can, pathname],
  );

  const name = locale === "ar" && user?.displayNameAr ? user.displayNameAr : user?.displayName ?? "";
  const title = locale === "ar" && user?.titleAr ? user.titleAr : user?.title ?? "";

  return (
    <>
      <AppShell
        header={
          <GovBar
            ministryName={t("app.ministry")}
            ministryNameSecondary={t("app.department")}
            platformName={t("admin:app.title")}
            locale={locale}
            onToggleLocale={toggle}
            logo={{ src: locale === "ar" ? `${import.meta.env.BASE_URL}brand/moet-logo-ar.png` : `${import.meta.env.BASE_URL}brand/moet-logo-en.png`, alt: t("app.ministry") }}
            onHome={go("/")}
            right={
              user ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onOpenPalette}
                    className="hidden h-9 items-center gap-2 rounded-xl border border-sand-200 bg-white/70 px-3 text-sm text-sand-600 transition-colors hover:border-aegold-300 hover:text-foreground md:flex"
                    aria-label={t("admin:shell.commandPalette")}
                  >
                    <Search className="size-4" />
                    <span className="w-40 text-start">{t("admin:shell.searchPlaceholder")}</span>
                    <Shortcut keys={[MOD, "K"]} />
                  </button>
                  <Tooltip content={t("admin:shell.environment")}>
                    <Badge variant={environment === "production" ? "success" : "warning"} size="sm" className="hidden uppercase tracking-wider sm:inline-flex" data-testid="environment-badge">
                      {environment}
                    </Badge>
                  </Tooltip>
                  <Tooltip content={t("admin:app.openBackoffice")}>
                    <Button asChild variant="ghost" size="icon-sm" aria-label={t("admin:app.openBackoffice")}>
                      <HandoffLink href={backofficeHref("/")}>
                        <ExternalLink />
                      </HandoffLink>
                    </Button>
                  </Tooltip>
                  <Tooltip content={t("admin:shell.help")}>
                    <Button variant="ghost" size="icon-sm" aria-label={t("admin:shell.help")} onClick={onOpenHelp}>
                      <CircleHelp />
                    </Button>
                  </Tooltip>
                  <div className="hidden text-end leading-tight md:block">
                    <p className="text-sm font-semibold">{name}</p>
                    <p className="text-[11px] text-muted-foreground">{title}</p>
                  </div>
                  <Avatar name={name} src={photo} />
                  <ReportIssue app="admin" />
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("admin:shell.signOut")}
                    onClick={() => {
                      clear();
                      navigate("/login");
                    }}
                  >
                    <LogOut />
                  </Button>
                </div>
              ) : null
            }
          />
        }
        collapsed={collapsed}
        sidebar={<SideNav sections={sections} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} footer={collapsed ? null : <AppVersion />} />}
        mobileSidebar={<SideNav sections={sections} footer={<AppVersion autoOpen={false} />} />}
      >
        <ConfirmProvider>
          <PageTransition routeKey={pathname}>
            <Breadcrumbs />
            {outlet}
          </PageTransition>
        </ConfirmProvider>
      </AppShell>
      <SessionGuard onSignedOut={signedOut} />
      <Toaster dir={dir} />
    </>
  );
}
